import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import Navbar from "../components/Navbar";
import LeadPostCard from "../components/LeadPostCard";
import EscrowTimeline from "../components/EscrowTimeline";
import SubscriptionBanner from "../components/SubscriptionBanner";
import { Plus, Briefcase, Building2, TrendingUp, ShieldCheck, Sparkles, Crown, Award, X, Clock } from "lucide-react";
import { toast } from "sonner";
import FileUploader from "../components/FileUploader";

function StatTile({ label, value, hint }) {
  return (
    <div className="bg-[#0a0a0a] border border-[#262626] rounded-xl p-5">
      <p className="overline">{label}</p>
      <p className="font-display text-3xl font-black text-white mt-2">{value}</p>
      {hint && <p className="text-xs text-[#737373] mt-1 font-mono">{hint}</p>}
    </div>
  );
}

function NewLeadModal({ onClose, onCreated }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    title: "", destination: "", travel_date: "", duration_days: 5, pax: 2,
    budget_usd: 5000, type: "leisure", segment: "group_leisure",
    description: "", requirements: "Flights, Hotels, Transfers",
    // Structured / MICE fields
    hotel_star: "4-star", room_config: "", meal_plan: "Breakfast", transport_notes: "",
    hall_capacity: "", hall_layout: "theatre", av_requirements: "",
    gala_dinner: false, team_building: false,
    inclusions_checklist: "", exclusions_checklist: "",
    bid_deadline: "",
  });
  const [attachments, setAttachments] = useState([]);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const isMice = form.segment === "mice" || form.type === "mice";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/leads", {
        title: form.title,
        destination: form.destination,
        travel_date: form.travel_date,
        duration_days: Number(form.duration_days),
        pax: Number(form.pax),
        budget_usd: Number(form.budget_usd),
        type: form.type,
        segment: isMice ? "mice" : form.segment,
        description: form.description,
        requirements: form.requirements.split(",").map((s) => s.trim()).filter(Boolean),
        hotel_star: form.hotel_star || null,
        room_config: form.room_config || null,
        meal_plan: form.meal_plan || null,
        transport_notes: form.transport_notes || null,
        hall_capacity: isMice && form.hall_capacity ? Number(form.hall_capacity) : null,
        hall_layout: isMice ? form.hall_layout : null,
        av_requirements: isMice ? form.av_requirements || null : null,
        gala_dinner: !!form.gala_dinner,
        team_building: !!form.team_building,
        inclusions_checklist: form.inclusions_checklist.split(",").map((s) => s.trim()).filter(Boolean),
        exclusions_checklist: form.exclusions_checklist.split(",").map((s) => s.trim()).filter(Boolean),
        attachments: attachments,
        bid_deadline: form.bid_deadline || null,
      });
      toast.success("RFP broadcast to matched partners");
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create RFP");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="new-lead-modal">
      <div className="bg-white border border-hairline rounded-md w-full max-w-3xl max-h-[92vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between p-6 border-b border-hairline bg-paper sticky top-0 z-10">
          <div>
            <p className="overline">Post an RFP</p>
            <h3 className="font-display text-2xl font-bold text-ink mt-1">Brief the partner network</h3>
          </div>
          <button onClick={onClose} className="text-muted hover:text-ink" data-testid="close-new-lead">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={submit} className="p-6 grid sm:grid-cols-2 gap-4">
          {/* Segment selector — drives whether MICE fields show */}
          <div className="sm:col-span-2">
            <label className="overline">Segment</label>
            <div className="flex flex-wrap gap-2 mt-2" data-testid="lead-segment-picker">
              {[
                { v: "group_leisure", l: "Group Leisure" },
                { v: "mice", l: "MICE · Meetings & Events" },
                { v: "fit", l: "FIT (Free Independent Traveller)" },
                { v: "incentive", l: "Incentive Group" },
              ].map((o) => (
                <button
                  type="button"
                  key={o.v}
                  onClick={() => setForm({ ...form, segment: o.v, type: o.v === "mice" ? "mice" : (form.type === "mice" ? "leisure" : form.type) })}
                  className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest rounded border ${form.segment === o.v ? "bg-ink text-white border-ink" : "bg-white text-ink border-hairline hover:border-ink"}`}
                  data-testid={`lead-segment-${o.v}`}
                >
                  {o.l}
                </button>
              ))}
            </div>
          </div>

          <div className="sm:col-span-2">
            <label className="overline">RFP Title</label>
            <input required value={form.title} onChange={upd("title")} className="w-full mt-2 px-4 py-3" data-testid="lead-title" placeholder={isMice ? "e.g. Pharma Conference · Dubai 2026" : "e.g. Maldives Honeymoon · 7N"} />
          </div>
          <div>
            <label className="overline">Destination</label>
            <input required value={form.destination} onChange={upd("destination")} className="w-full mt-2 px-4 py-3" data-testid="lead-destination" placeholder="Dubai, UAE" />
          </div>
          <div>
            <label className="overline">Travel start date</label>
            <input type="date" required value={form.travel_date} onChange={upd("travel_date")} className="w-full mt-2 px-4 py-3" data-testid="lead-date" />
          </div>
          <div>
            <label className="overline">Pax</label>
            <input type="number" min={1} value={form.pax} onChange={upd("pax")} className="w-full mt-2 px-4 py-3" data-testid="lead-pax" />
          </div>
          <div>
            <label className="overline">Days</label>
            <input type="number" min={1} value={form.duration_days} onChange={upd("duration_days")} className="w-full mt-2 px-4 py-3" data-testid="lead-days" />
          </div>
          <div>
            <label className="overline">Budget (USD)</label>
            <input type="number" min={0} value={form.budget_usd} onChange={upd("budget_usd")} className="w-full mt-2 px-4 py-3" data-testid="lead-budget" />
          </div>
          <div>
            <label className="overline">Bid deadline</label>
            <input type="datetime-local" value={form.bid_deadline} onChange={upd("bid_deadline")} className="w-full mt-2 px-4 py-3" data-testid="lead-deadline" />
          </div>

          {/* Hotel & F&B block */}
          <div className="sm:col-span-2 mt-2 pt-4 border-t border-hairline">
            <p className="overline text-brand">Hotel · Room · Meals</p>
          </div>
          <div>
            <label className="overline">Hotel star</label>
            <select value={form.hotel_star} onChange={upd("hotel_star")} className="w-full mt-2 px-4 py-3" data-testid="lead-hotel-star">
              {["3-star", "4-star", "5-star", "luxury", "boutique"].map((v) => <option key={v}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="overline">Meal plan</label>
            <select value={form.meal_plan} onChange={upd("meal_plan")} className="w-full mt-2 px-4 py-3" data-testid="lead-meal-plan">
              {["Room Only", "Breakfast", "Half Board", "Full Board", "All Inclusive"].map((v) => <option key={v}>{v}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="overline">Room config</label>
            <input value={form.room_config} onChange={upd("room_config")} className="w-full mt-2 px-4 py-3" data-testid="lead-room-config" placeholder="e.g. 50 twin, 10 single, 2 suites" />
          </div>
          <div className="sm:col-span-2">
            <label className="overline">Transport / Transfers</label>
            <input value={form.transport_notes} onChange={upd("transport_notes")} className="w-full mt-2 px-4 py-3" data-testid="lead-transport" placeholder="e.g. 45-seater AC coach × 2, airport transfers × 4" />
          </div>

          {/* MICE-only block */}
          {isMice && (
            <>
              <div className="sm:col-span-2 mt-2 pt-4 border-t border-hairline">
                <p className="overline text-brand">MICE Specifics</p>
              </div>
              <div>
                <label className="overline">Hall capacity (pax)</label>
                <input type="number" min={0} value={form.hall_capacity} onChange={upd("hall_capacity")} className="w-full mt-2 px-4 py-3" data-testid="lead-hall-capacity" placeholder="e.g. 250" />
              </div>
              <div>
                <label className="overline">Hall layout</label>
                <select value={form.hall_layout} onChange={upd("hall_layout")} className="w-full mt-2 px-4 py-3" data-testid="lead-hall-layout">
                  {["theatre", "classroom", "u-shape", "banquet", "cabaret", "boardroom"].map((v) => <option key={v}>{v}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="overline">AV requirements</label>
                <textarea rows={2} value={form.av_requirements} onChange={upd("av_requirements")} className="w-full mt-2 px-4 py-3" data-testid="lead-av" placeholder="e.g. LED wall 6×3m, 2 wireless mics, translation booth" />
              </div>
              <label className="flex items-center gap-2 text-sm text-ink" data-testid="lead-gala-toggle">
                <input type="checkbox" checked={form.gala_dinner} onChange={upd("gala_dinner")} className="w-4 h-4" />
                <span>Include gala dinner</span>
              </label>
              <label className="flex items-center gap-2 text-sm text-ink" data-testid="lead-teambuilding-toggle">
                <input type="checkbox" checked={form.team_building} onChange={upd("team_building")} className="w-4 h-4" />
                <span>Include team-building activity</span>
              </label>
            </>
          )}

          {/* Description + inclusions */}
          <div className="sm:col-span-2 mt-2 pt-4 border-t border-hairline">
            <p className="overline text-brand">Brief · Inclusions · Exclusions</p>
          </div>
          <div className="sm:col-span-2">
            <label className="overline">Description</label>
            <textarea required rows={3} value={form.description} onChange={upd("description")} className="w-full mt-2 px-4 py-3" data-testid="lead-desc" placeholder="Objectives, audience, must-haves." />
          </div>
          <div className="sm:col-span-2">
            <label className="overline">Must-have inclusions (comma-separated)</label>
            <input value={form.inclusions_checklist} onChange={upd("inclusions_checklist")} className="w-full mt-2 px-4 py-3" data-testid="lead-incl-checklist" placeholder="Conference Hall, F&B, AV, Airport Transfers, Gala Dinner" />
          </div>
          <div className="sm:col-span-2">
            <label className="overline">Explicit exclusions · what buyer says NO to</label>
            <input value={form.exclusions_checklist} onChange={upd("exclusions_checklist")} className="w-full mt-2 px-4 py-3" data-testid="lead-excl-checklist" placeholder="Personal expenses, Visa fees, Tips" />
          </div>
          <div className="sm:col-span-2">
            <label className="overline">Legacy requirements (comma-separated)</label>
            <input value={form.requirements} onChange={upd("requirements")} className="w-full mt-2 px-4 py-3" data-testid="lead-reqs" />
          </div>

          <div className="sm:col-span-2 mt-2 pt-4 border-t border-hairline">
            <p className="overline text-brand">Attachments · RFP PDF, floor plans, prior quotes</p>
            <div className="mt-3">
              <FileUploader value={attachments} onChange={setAttachments} purpose="rfp" testid="lead-attachments" />
            </div>
          </div>

          <div className="sm:col-span-2 flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="btn-ghost text-sm">Cancel</button>
            <button type="submit" disabled={busy} className="btn-primary text-sm" data-testid="submit-new-lead">
              {busy ? "Broadcasting…" : "Broadcast to Top-10 partners"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [escrows, setEscrows] = useState([]);
  const [stats, setStats] = useState({});
  const [showNew, setShowNew] = useState(false);
  const [tab, setTab] = useState((user?.role === "dmc" || user?.role === "mice_operator") ? "feed" : "mine");

  const load = () => {
    if (user.role === "agent") {
      api.get("/leads?scope=mine").then((r) => setLeads(r.data || []));
    } else if (user.role === "dmc" || user.role === "mice_operator") {
      const scope = tab === "feed" ? "all" : "mine";
      api.get("/leads", { params: { scope } }).then((r) => setLeads(r.data || []));
    } else {
      api.get("/leads").then((r) => setLeads(r.data || []));
    }
    api.get("/escrow/me").then((r) => setEscrows(r.data || []));
    api.get("/admin/stats").then((r) => setStats(r.data || {})).catch(() => {});
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user, tab]);

  const isAgent = user?.role === "agent";
  const isDmc = user?.role === "dmc" || user?.role === "mice_operator";

  return (
    <div className="App" data-testid="dashboard-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <SubscriptionBanner />
        {/* Pending review banner for unverified DMC / MICE operators */}
        {(user?.role === "dmc" || user?.role === "mice_operator") && !user?.verified && (
          <div className="pending-banner mb-6 flex items-start gap-3" data-testid="dashboard-pending-banner">
            <Clock className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <strong>Your account is under Trawell Ops review (24–48 hrs).</strong> You can preview RFPs, but bid submission
              is locked until you're approved. We'll email <span className="font-mono">{user?.email}</span> the moment you're activated.
            </div>
          </div>
        )}
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="overline flex items-center gap-2">
              {isDmc ? <Building2 className="w-3.5 h-3.5" /> : <Briefcase className="w-3.5 h-3.5" />}
              {user?.role.toUpperCase()} dashboard
            </p>
            <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-white mt-3 uppercase">
              Welcome, <span className="text-brand">{user?.name?.split(" ")[0]}</span>
            </h1>
            <p className="text-sm text-[#a3a3a3] mt-2">
              {isAgent && "Post a lead, watch the bids stream in, accept the best one."}
              {isDmc && "Live leads from the global agent network. Bid fast to win."}
              {user?.role === "admin" && "Platform overview & controls."}
            </p>
          </div>
          {isAgent && (
            <button onClick={() => setShowNew(true)} className="btn-primary" data-testid="new-lead-btn">
              <Plus className="w-4 h-4" /> Post a lead
            </button>
          )}
          {isDmc && !user.premium && (
            <button
              onClick={async () => {
                await api.post("/dmcs/upgrade-premium");
                toast.success("You're now Premium ✨");
                window.location.reload();
              }}
              className="btn-primary"
              data-testid="upgrade-premium-btn"
            >
              <Crown className="w-4 h-4" /> Go Premium
            </button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8" data-testid="stat-tiles">
          <StatTile label="Open leads" value={stats.leads_open ?? "—"} hint="across the network" />
          <StatTile label="Total bids" value={stats.bids ?? "—"} hint="ever placed" />
          <StatTile label="Premium DMCs" value={stats.premium_dmcs ?? "—"} hint="verified partners" />
          <StatTile label="Escrow held" value={`$${Math.round(stats.escrow_held_usd || 0).toLocaleString()}`} hint="10% of awarded value" />
        </div>

        {/* DMC Tabs */}
        {isDmc && (
          <div className="inline-flex bg-[#0a0a0a] border border-[#262626] rounded-md mt-10 p-1" data-testid="dmc-tabs">
            {[
              { v: "feed", label: "Live leads", icon: <TrendingUp className="w-4 h-4" /> },
              { v: "mine", label: "My bids", icon: <Award className="w-4 h-4" /> },
            ].map((t) => (
              <button
                key={t.v}
                onClick={() => setTab(t.v)}
                className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm transition-colors ${
                  tab === t.v ? "bg-brand text-white font-bold" : "text-[#a3a3a3] hover:text-white"
                }`}
                data-testid={`tab-${t.v}`}
              >
                {t.icon} {t.label}
              </button>
            ))}
          </div>
        )}

        {/* Lead grid */}
        <section className="mt-8 grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl font-bold text-white">
                {isAgent ? "Your leads" : isDmc ? (tab === "feed" ? "Open leads in the network" : "Leads you've bid on") : "All leads"}
              </h2>
              <span className="text-xs font-mono text-[#737373] uppercase tracking-widest">{leads.length} items</span>
            </div>
            {leads.length === 0 ? (
              <div className="bg-[#0a0a0a] border border-[#262626] rounded-xl p-10 text-center" data-testid="leads-empty">
                <Sparkles className="w-8 h-8 text-brand mx-auto" />
                <p className="text-white font-display text-xl mt-3">Nothing here yet.</p>
                <p className="text-sm text-[#a3a3a3] mt-2">
                  {isAgent ? "Post your first lead — DMCs are watching." : "Check back in a moment, the network is active."}
                </p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4" data-testid="leads-grid">
                {leads.map((l) => (
                  <LeadPostCard key={l.lead_id} lead={l} />
                ))}
              </div>
            )}
          </div>

          {/* Side: Escrow */}
          <aside className="space-y-6">
            <div>
              <h2 className="font-display text-xl font-bold text-white mb-4 flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-brand" /> Escrow
              </h2>
              {escrows.length === 0 ? (
                <div className="bg-[#0a0a0a] border border-[#262626] rounded-xl p-6 text-center text-sm text-[#737373]" data-testid="escrow-empty">
                  No escrow yet. Awarded bookings appear here.
                </div>
              ) : (
                <div className="space-y-4">
                  {escrows.slice(0, 3).map((e) => (
                    <EscrowTimeline key={e.escrow_id} escrow={e} onUpdated={load} />
                  ))}
                </div>
              )}
            </div>
          </aside>
        </section>
      </div>
      {showNew && <NewLeadModal onClose={() => setShowNew(false)} onCreated={load} />}
    </div>
  );
}
